import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EMPTY, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { debounce } from 'lodash';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import { ContactTableComponent } from '../../../../components/tables/contact-table/contact-table.component';
import { IContact, IOrganization, IPagination, ITag } from '../../../../types/contactModule';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { AssignMultipleContactsToOrganizationsComponent } from '../../../../components/tables/assign-multiple-contacts-to-organizations/assign-multiple-contacts-to-organizations.component';
import { AssignMultipleContactsToTagsComponent } from '../../../../components/tables/assign-multiple-contacts-to-tags/assign-multiple-contacts-to-tags.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { CreateContactFormComponent } from '../../../../components/forms/create-contact-form/create-contact-form.component';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { getCountryCode } from '../../../../../utils/utils';
import { formatCount } from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';

export type TContactSort =
  | 'fullName,asc'
  | 'fullName,desc'
  | 'createdAt,asc'
  | 'createdAt,desc'
  | 'modifiedAt,asc'
  | 'modifiedAt,desc';

type TAddedFilter = 'any' | '7' | '30' | '90' | '365';
type TSearchField = 'fullName' | 'emailAddress' | 'primaryContactNumber' | 'country' | 'gender';

/** The export pages through the list endpoint; past this many rows, narrow the list first. */
const EXPORT_CAP = 10000;
const EXPORT_PAGE_SIZE = 500;
/** Tags and organizations offered in the filter menu (first ones by name). */
const FILTER_OPTION_LIMIT = 200;

const SEARCH_PARAM: Record<TSearchField, string> = {
  fullName: 'fullName.contains',
  emailAddress: 'emailAddress.contains',
  primaryContactNumber: 'primaryContactNumber.contains',
  country: 'country.contains',
  gender: 'gender.equals',
};

@Component({
  selector: 'app-contact-list',
  standalone: true,
  imports: [
    SearchInputComponent,
    ContactTableComponent,
    AssignMultipleContactsToOrganizationsComponent,
    AssignMultipleContactsToTagsComponent,
    SortDropdownComponent,
    PromptCardComponent,
    CreateContactFormComponent,
    NgClickOutsideDirective,
  ],
  templateUrl: './contact-list.component.html',
  styleUrl: './contact-list.component.css',
})
export class ContactListComponent implements OnInit, OnDestroy {
  contactsService = inject(ContactsService);
  router = inject(Router);
  private permissionsService = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);

  readonly formatCount = formatCount;
  readonly exportCap = EXPORT_CAP;

  // for multiple selected contacts
  showAssignMultipleContactsToOrg: boolean = false;
  showAssignMultipleContactsToTags: boolean = false;
  showDeleteSelectedPrompt: boolean = false;
  selectedContacts: IContact[] = [];

  contacts: IContact[] = [];
  pagination: IPagination | null = null;
  /** True once the first page has come back, so the zero state never flashes. */
  hasLoaded = false;

  searchTerm: string = '';
  private searchField: TSearchField = 'fullName';
  /** A name search that finds nobody retries once as a country or gender search. */
  private searchFallback: TSearchField | null = null;
  private awaitingSearchResult = false;

  sortCriteria: TContactSort = 'fullName,asc';

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'fullName',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  // ── Filters ──
  showFilters = false;
  addedFilter: TAddedFilter = 'any';
  tagFilter: string = '';
  organizationFilter: string = '';
  tagOptions: ITag[] = [];
  organizationOptions: IOrganization[] = [];
  filterOptionsLoading = false;
  filterOptionsError = '';
  tagOptionsTruncated = false;
  organizationOptionsTruncated = false;
  private filterOptionsLoaded = false;

  readonly addedOptions: { value: TAddedFilter; label: string }[] = [
    { value: 'any', label: 'Any time' },
    { value: '7', label: 'In the last 7 days' },
    { value: '30', label: 'In the last 30 days' },
    { value: '90', label: 'In the last 90 days' },
    { value: '365', label: 'In the last 12 months' },
  ];

  /** Search, filters and sort, without page or size. A new object on each change. */
  queryParams: Record<string, any> = { sort: this.sortCriteria };

  // ── Export ──
  exporting = false;
  exportDone = 0;
  exportTarget = 0;
  exportNotice = '';
  exportError = '';
  private exportSub?: Subscription;

  // Zero state: add one contact from here.
  showCreateContact = false;

  @ViewChild(ContactTableComponent, { static: false })
  contactTable?: ContactTableComponent;

  @ViewChild(SearchInputComponent, { static: false })
  searchInput?: SearchInputComponent;

  @ViewChild('filterTrigger') private filterTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('filterPanel') private filterPanel?: ElementRef<HTMLElement>;

  /** Debounced list fetch after search input changes. */
  private readonly debouncedSearch = debounce(() => this.searchContacts(), 400);

  get totalElements(): number {
    return this.pagination?.totalElements ?? 0;
  }

  get filtersActive(): boolean {
    return this.addedFilter !== 'any' || !!this.tagFilter || !!this.organizationFilter;
  }

  get activeFilterCount(): number {
    return (this.addedFilter !== 'any' ? 1 : 0) + (this.tagFilter ? 1 : 0) + (this.organizationFilter ? 1 : 0);
  }

  /** No contacts at all: show the ways to add some instead of an empty table. */
  get isEmptyAudience(): boolean {
    return (
      this.hasLoaded &&
      !this.contactsService.isFetching &&
      !this.searchTerm.trim() &&
      !this.filtersActive &&
      this.totalElements === 0 &&
      this.contacts.length === 0
    );
  }

  get addedLabel(): string {
    return this.addedOptions.find((option) => option.value === this.addedFilter)?.label ?? '';
  }

  get tagLabel(): string {
    return this.tagOptions.find((tag) => String(tag.id) === this.tagFilter)?.name || 'Selected tag';
  }

  get organizationLabel(): string {
    return (
      this.organizationOptions.find((org) => String(org.id) === this.organizationFilter)?.name ||
      'Selected organization'
    );
  }

  get countLine(): string {
    const total = this.totalElements;
    const noun = total === 1 ? 'contact' : 'contacts';
    if (this.searchTerm.trim() || this.filtersActive) return `${formatCount(total)} ${noun} match`;
    return `${formatCount(total)} ${noun}`;
  }

  get exportLabel(): string {
    if (!this.totalElements) return 'Export CSV';
    return this.searchTerm.trim() || this.filtersActive ? 'Export matches' : 'Export CSV';
  }

  get exportProgress(): number {
    return this.exportTarget ? Math.min(1, this.exportDone / this.exportTarget) : 0;
  }

  /** For screen readers: once when the export starts, then how it ended (not every page). */
  get exportStatus(): string {
    if (this.exporting) return 'Exporting contacts';
    return this.exportError || this.exportNotice;
  }

  /** Which bulk actions and ways to add contacts are offered, from permissions. */
  can = { manageGroups: false, delete: false, create: false, import: false };

  /** Zero state: how many ways in this person is allowed to use. */
  get zeroPathCount(): number {
    return (this.can.create ? 2 : 0) + (this.can.import ? 1 : 0);
  }

  get zeroStateLine(): string {
    const then = 'You can message them as soon as they are saved.';
    if (this.can.create && this.can.import) return `Add one, add several, or import a spreadsheet. ${then}`;
    if (this.can.create) return `Add one or add several. ${then}`;
    if (this.can.import) return `Import a spreadsheet. ${then}`;
    return 'Contacts will show here once someone with permission to add them does.';
  }

  // ── Search ──
  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    const normalizedTerm = this.searchTerm.trim();
    this.searchField = 'fullName';
    this.searchFallback = null;

    if (normalizedTerm.includes('@')) {
      this.searchField = 'emailAddress';
    } else if (this.looksLikePhoneSearch(normalizedTerm)) {
      this.searchField = 'primaryContactNumber';
    } else if (normalizedTerm) {
      // Names first; a name search that finds nobody tries gender or country.
      if (this.looksLikeGenderSearch(normalizedTerm)) this.searchFallback = 'gender';
      else if (this.looksLikeCountrySearch(normalizedTerm)) this.searchFallback = 'country';
    }
    this.debouncedSearch();
  }

  clearSearchAndFilters() {
    this.debouncedSearch.cancel();
    this.searchInput?.writeValue('');
    this.searchTerm = '';
    this.searchField = 'fullName';
    this.searchFallback = null;
    this.addedFilter = 'any';
    this.tagFilter = '';
    this.organizationFilter = '';
    this.refresh();
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
    this.contactTable?.resetSelectedContacts();
  }

  searchContacts() {
    this.awaitingSearchResult = !!this.searchFallback;
    this.refresh();
  }

  /** Rebuilds the query from search, filters and sort, then loads page one. */
  private refresh() {
    const params: Record<string, any> = { sort: this.sortCriteria };
    const term = this.searchTerm.trim();
    if (term) {
      params[SEARCH_PARAM[this.searchField]] =
        this.searchField === 'gender' ? term.toUpperCase() : term;
    }
    if (this.addedFilter !== 'any') {
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      since.setDate(since.getDate() - Number(this.addedFilter));
      params['createdAt.greaterThanOrEqual'] = since.toISOString();
    }
    if (this.tagFilter) params['entityTagId.equals'] = this.tagFilter;
    if (this.organizationFilter) params['companyEntityId.equals'] = this.organizationFilter;

    this.queryParams = params;
    this.contactTable?.resetToPageZero();
    const size = this.contactTable?.pageSize ?? 10;
    this.contactsService.fetchContacts({ ...params, page: 0, size });
  }

  private looksLikePhoneSearch(term: string): boolean {
    // Accept + prefix, digits, spaces, and separators commonly used in entered phone numbers.
    const compact = term.replace(/[\s\-()]/g, '');
    return /^\+?\d+$/.test(compact);
  }

  private looksLikeGenderSearch(term: string): boolean {
    return ['MALE', 'FEMALE', 'OTHER'].includes(term.toUpperCase());
  }

  private looksLikeCountrySearch(term: string): boolean {
    try {
      return !!getCountryCode(term)?.countryCode;
    } catch {
      return false;
    }
  }

  updateSortCriteria(criteria: TContactSort) {
    this.sortCriteria = criteria;
    this.refresh();
  }

  // ── Filters ──
  toggleFilters() {
    if (this.showFilters) {
      this.closeFilters();
      return;
    }
    this.showFilters = true;
    this.loadFilterOptions();
    // Move into the dialog once it has rendered.
    setTimeout(() => this.filterPanel?.nativeElement.querySelector<HTMLElement>('select')?.focus());
  }

  /** Closes the panel; focus inside it goes back to the trigger instead of dropping to the page. */
  closeFilters() {
    if (!this.showFilters) return;
    const panel = this.filterPanel?.nativeElement;
    const focusWasInside =
      !!panel && typeof document !== 'undefined' && panel.contains(document.activeElement);
    this.showFilters = false;
    if (focusWasInside) this.filterTrigger?.nativeElement.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.showFilters) this.closeFilters();
  }

  setAddedFilter(value: string) {
    this.addedFilter = (value as TAddedFilter) || 'any';
    this.refresh();
  }

  setTagFilter(value: string) {
    this.tagFilter = value || '';
    this.refresh();
  }

  setOrganizationFilter(value: string) {
    this.organizationFilter = value || '';
    this.refresh();
  }

  clearFilter(which: 'added' | 'tag' | 'organization') {
    if (which === 'added') this.addedFilter = 'any';
    if (which === 'tag') this.tagFilter = '';
    if (which === 'organization') this.organizationFilter = '';
    this.refresh();
  }

  clearAllFilters() {
    this.addedFilter = 'any';
    this.tagFilter = '';
    this.organizationFilter = '';
    this.refresh();
  }

  loadFilterOptions(force = false) {
    if ((this.filterOptionsLoaded && !force) || this.filterOptionsLoading) return;
    this.filterOptionsLoading = true;
    this.filterOptionsError = '';
    const params = { page: 0, size: FILTER_OPTION_LIMIT, sort: 'name,asc' };
    forkJoin({
      tags: this.contactsService.searchTags(params),
      organizations: this.contactsService.searchOrganizations(params),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tags, organizations }) => {
          this.tagOptions = tags.items;
          this.organizationOptions = organizations.items;
          this.tagOptionsTruncated = (tags.itemPagination?.totalElements ?? 0) > tags.items.length;
          this.organizationOptionsTruncated =
            (organizations.itemPagination?.totalElements ?? 0) > organizations.items.length;
          this.filterOptionsLoaded = true;
          this.filterOptionsLoading = false;
        },
        error: () => {
          this.filterOptionsLoading = false;
          this.filterOptionsError = 'Tags and organizations did not load. Try again.';
        },
      });
  }

  // ── Bulk actions ──
  openAssignMultipleContactsToOrg() {
    this.showAssignMultipleContactsToOrg = true;
  }
  closeAssignMultipleContactsToOrg() {
    this.showAssignMultipleContactsToOrg = false;
  }
  openAssignMultipleContactsToTags() {
    this.showAssignMultipleContactsToTags = true;
  }
  closeAssignMultipleContactsToTags() {
    this.showAssignMultipleContactsToTags = false;
  }

  handleContactsSelection(selectedContacts: IContact[]) {
    this.selectedContacts = [...selectedContacts];
  }

  get reactiveParams() {
    return this.queryParams;
  }

  get selectionLabel(): string {
    const count = this.selectedContacts.length;
    return count === 1 ? '1 contact selected' : `${formatCount(count)} contacts selected`;
  }

  sendSMStoSelectedContacts() {
    const params = { items: JSON.stringify(this.selectedContacts) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
    this.resetSelectedContacts();
  }

  openDeleteSelectedPrompt() {
    if (this.selectedContacts.length) this.showDeleteSelectedPrompt = true;
  }

  closeDeleteSelectedPrompt() {
    this.showDeleteSelectedPrompt = false;
  }

  deleteSelectedContacts() {
    const count = this.selectedContacts.length;
    this.contactsService
      .deleteSelectedContacts(this.selectedContacts.map((el) => el.uuid as string))
      .subscribe({
        next: () => {
          // The service refreshes the list with the current search, sort and page.
          this.resetSelectedContacts();
          this.closeDeleteSelectedPrompt();
        },
        error: () => {
          this.closeDeleteSelectedPrompt();
          this.contactsService.showError(
            `${count === 1 ? 'The contact was' : `The ${count} contacts were`} not deleted. Try again.`
          );
        },
      });
  }

  handleAction(action: string) {
    if (this.selectedContacts.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'move-to-organization':
        this.openAssignMultipleContactsToOrg();
        break;
      case 'add-tags':
        this.openAssignMultipleContactsToTags();
        break;
      case 'delete':
        this.openDeleteSelectedPrompt();
        break;
    }
  }

  // ── Zero state ──
  openCreateContact() {
    this.showCreateContact = true;
  }

  closeCreateContact() {
    this.showCreateContact = false;
  }

  goToCreateMultipleContactsPage() {
    this.router.navigate(['/organization/contacts/multiple-contacts']);
  }

  goToCsvUpload() {
    this.router.navigate(['/organization/contacts/csv']);
  }

  // ── Export ──
  exportContacts() {
    if (this.exporting || !this.totalElements) return;
    const base = { ...this.queryParams };
    const filtered = !!this.searchTerm.trim() || this.filtersActive;
    const total = this.totalElements;
    this.exportTarget = Math.min(total, EXPORT_CAP);
    this.exportDone = 0;
    this.exportNotice = '';
    this.exportError = '';
    this.exporting = true;

    // Pages are separate queries: break ties on the sort (two "Ada"s) by id,
    // so no contact appears on two pages or on none.
    const sort = [base['sort'] ?? this.sortCriteria, 'id,asc'];
    const page = (index: number) =>
      this.contactsService
        .listContactsPage({ ...base, sort, page: index, size: EXPORT_PAGE_SIZE })
        .pipe(map((res) => ({ index, ...res })));

    let collected = 0;
    this.exportSub = page(0)
      .pipe(
        expand((res) => {
          collected += res.items.length;
          this.exportDone = Math.min(collected, this.exportTarget);
          const totalPages = res.itemPagination?.totalPages ?? 0;
          const more = res.items.length > 0 && res.index + 1 < totalPages && collected < this.exportTarget;
          return more ? page(res.index + 1) : EMPTY;
        }),
        reduce((rows: IContact[], res) => rows.concat(res.items), []),
        catchError(() => {
          this.exportError = 'The export stopped before it finished. Check your connection and try again.';
          this.exporting = false;
          return of(null);
        })
      )
      .subscribe((rows) => {
        if (!rows) return;
        const kept = rows.slice(0, EXPORT_CAP);
        downloadCsv(csvFileName('contacts', filtered ? 'filtered' : null), this.toContactsCsv(kept));
        this.exporting = false;
        if (total > EXPORT_CAP) {
          this.exportNotice = `Exported the first ${formatCount(EXPORT_CAP)} of ${formatCount(total)} contacts. Narrow the list with search or filters to export the rest.`;
        } else {
          this.contactsService.showSuccess(
            `Exported ${formatCount(kept.length)} ${kept.length === 1 ? 'contact' : 'contacts'}.`
          );
        }
      });
  }

  cancelExport() {
    this.exportSub?.unsubscribe();
    this.exporting = false;
    this.exportDone = 0;
  }

  dismissExportNotice() {
    this.exportNotice = '';
    this.exportError = '';
  }

  private toContactsCsv(rows: IContact[]): string {
    const names = (items: { name?: string | null }[] | undefined) =>
      (items ?? []).map((item) => item?.name).filter(Boolean).join('; ');
    return toCsv(rows, [
      { label: 'First name', value: (c) => c.firstName },
      { label: 'Last name', value: (c) => c.lastName },
      { label: 'Phone number', value: (c) => c.primaryContactNumber },
      { label: 'Email', value: (c) => c.emailAddress },
      { label: 'Gender', value: (c) => c.gender },
      { label: 'Birth date', value: (c) => (c.birthDate ? String(c.birthDate).slice(0, 10) : '') },
      { label: 'City', value: (c) => c.city },
      { label: 'Country', value: (c) => c.country },
      { label: 'Organizations', value: (c) => names(c.companyEntities) },
      { label: 'Tags', value: (c) => names(c.entityTags) },
      { label: 'Source', value: (c) => c.origin },
      { label: 'Added', value: (c) => c.createdAt },
    ]);
  }

  ngOnInit() {
    this.refresh();

    this.contactsService.getContacts
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contacts: IContact[]) => {
        this.contacts = contacts ?? [];
      });

    // The subject replays its last value on subscribe (maybe from an earlier
    // visit); only answers that arrive after that count as loaded.
    let replaying = true;
    this.contactsService.getContactsPaginationData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pagination: IPagination) => {
        if (!pagination) return;
        this.pagination = pagination;
        if (replaying) return;
        this.hasLoaded = true;

        // A name search that found nobody: try the term as a gender or country.
        if (this.awaitingSearchResult) {
          this.awaitingSearchResult = false;
          if (!pagination.totalElements && this.searchFallback && this.searchField === 'fullName') {
            this.searchField = this.searchFallback;
            this.searchFallback = null;
            this.refresh();
            return;
          }
        }

        // Deleting the last rows of the last page: step back to the new last page.
        const total = pagination.totalElements ?? 0;
        const lastPage = Math.max(0, (pagination.totalPages ?? 1) - 1);
        if (total > 0 && (pagination.pageNumber ?? 0) > lastPage) {
          this.contactTable?.updateContactsPage(lastPage);
        }
      });
    replaying = false;

    combineLatest([
      this.permissionsService.hasPermission('CONTACT_MANAGEMENT', 'manage-contact-groups'),
      this.permissionsService.hasPermission('CONTACT_MANAGEMENT', 'delete-contacts'),
      this.permissionsService.hasPermission('CONTACT_MANAGEMENT', 'create-contacts'),
      this.permissionsService.hasPermission('CONTACT_MANAGEMENT', 'import-contacts-csv-excel'),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([canManageGroups, canDelete, canCreate, canImport]) => {
        this.can = {
          manageGroups: !!canManageGroups,
          delete: !!canDelete,
          create: !!canCreate,
          import: !!canImport,
        };
      });
  }

  ngOnDestroy() {
    this.debouncedSearch.cancel();
    this.exportSub?.unsubscribe();
  }
}
